import { Partido, PartidoJSON } from '../Partido/Partido';
import { Categoria, CategoriaJSON } from '../Categoria/Categoria';
import { Division, DivisionJSON } from '../Division/Division';
import { Federacion, FederacionJSON } from '../Federacion/Federacion';
import { Temporada, TemporadaJSON } from '../Temporada/Temporada';
import { Fase, FaseJSON } from '../Fase/Fase';
import {
  TipoDefinicionXTorneo,
  TipoDefinicionXTorneoJSON,
} from '../TipoDefinicionXTorneo/TipoDefinicionXTorneo';
import { TorneoTipo, TorneoTipoJSON } from '../TorneoTipo/TorneoTipo';

export interface TorneoJSON {
  tor_id: string | null;
  tor_nombre: string;
  tor_fecha_inicio: Date;
  tor_temid: number;
  tor_catid: number;
  tor_divid: number;
  tor_rama: string;
  tor_cant_ascensos: number;
  tor_cant_descensos: number;
  tor_descripcion: any;
  tor_ttid: number;
  tor_fedid: number;
  tor_es_temporal: boolean;
  tor_aplico_para_tira_inferiores: boolean;
  tor_tiene_lista_buena_fe: boolean;
  tor_cant_max_refuerzos: number;
  tor_multifederacion: boolean;
  faseList: FaseJSON[];
  tipoDefinicionXTorneoList: TipoDefinicionXTorneoJSON[];
  categoria: CategoriaJSON | null;
  division: DivisionJSON | null;
  torneoTipo: TorneoTipoJSON | null;
  federacion: FederacionJSON | null;
  temporada: TemporadaJSON | null;
}

export class Torneo {
  protected constructor(
    private id: string | null,
    private nombre: string,
    private fechaInicio: Date,
    private temid: number,
    private catid: number,
    private divid: number,
    private rama: string,
    private cantAscensos: number,
    private cantDescensos: number,
    private descripcion: any,
    private ttid: number,
    private fedid: number,
    private esTemporal: boolean,
    private aplicoParaTiraInferiores: boolean,
    private tieneListaBuenaFe: boolean,
    private cantMaxRefuerzos: number,
    private multifederacion: boolean,
    private faseList: Fase[],
    private tipoDefinicionXTorneoList: TipoDefinicionXTorneo[],
    private categoria: Categoria | null,
    private division: Division | null,
    private torneoTipo: TorneoTipo | null,
    private federacion: Federacion | null,
    private temporada: Temporada | null
  ) {}

  static createFrom(
    id: string | null,
    nombre: string,
    fechaInicio: Date,
    temid: number,
    catid: number,
    divid: number,
    rama: string,
    cantAscensos: number,
    cantDescensos: number,
    descripcion: any,
    ttid: number,
    fedid: number,
    esTemporal: boolean,
    aplicoParaTiraInferiores: boolean,
    tieneListaBuenaFe: boolean,
    cantMaxRefuerzos: number,
    multifederacion: boolean,

    faseList: Fase[],
    tipoDefinicionXTorneoList: TipoDefinicionXTorneo[],
    categoria: Categoria | null,
    division: Division | null,
    torneoTipo: TorneoTipo | null,
    federacion: Federacion | null,
    temporada: Temporada | null
  ) {
    return new this(
      id,
      nombre,
      fechaInicio,
      temid,
      catid,
      divid,
      rama,
      cantAscensos,
      cantDescensos,
      descripcion,
      ttid,
      fedid,
      esTemporal,
      aplicoParaTiraInferiores,
      tieneListaBuenaFe,
      cantMaxRefuerzos,
      multifederacion,
      faseList,
      tipoDefinicionXTorneoList,
      categoria,
      division,
      torneoTipo,
      federacion,
      temporada
    );
  }

  isIdentifiedBy(anId: string): boolean {
    return this.id === anId;
  }

  isIdentifiedAs(aEntity: Torneo) {
    if (this.id) {
      return aEntity.isIdentifiedBy(this.id);
    } else {
      return false;
    }
  }

  static fromJSON(entity: TorneoJSON): any {
    return Torneo.createFrom(
      entity.tor_id,
      entity.tor_nombre,
      new Date(entity.tor_fecha_inicio),
      entity.tor_temid,
      entity.tor_catid,
      entity.tor_divid,
      entity.tor_rama,
      entity.tor_cant_ascensos,
      entity.tor_cant_descensos,
      entity.tor_descripcion,
      entity.tor_ttid,
      entity.tor_fedid,
      entity.tor_es_temporal,
      entity.tor_aplico_para_tira_inferiores,
      entity.tor_tiene_lista_buena_fe,
      entity.tor_cant_max_refuerzos,
      entity.tor_multifederacion,
      entity.faseList ? entity.faseList.map((p) => Fase.fromJSON(p)) : [],
      entity.tipoDefinicionXTorneoList
        ? entity.tipoDefinicionXTorneoList.map((td) => TipoDefinicionXTorneo.fromJSON(td))
        : [],
      entity.categoria ? Categoria.fromJSON(entity.categoria) : null,
      entity.division ? Division.fromJSON(entity.division) : null,
      entity.torneoTipo ? TorneoTipo.fromJSON(entity.torneoTipo) : null,
      entity.federacion ? Federacion.fromJSON(entity.federacion) : null,
      entity.temporada ? Temporada.fromJSON(entity.temporada) : null
    );
  }

  toJSON(): TorneoJSON {
    return {
      tor_id: this.getId(),
      tor_nombre: this.getNombre(),
      tor_fecha_inicio: this.getFechaInicio(),
      tor_temid: this.getTemid(),
      tor_catid: this.getCatid(),
      tor_divid: this.getDivid(),
      tor_rama: this.getRama(),
      tor_cant_ascensos: this.getCantAscensos(),
      tor_cant_descensos: this.getCantDescensos(),
      tor_descripcion: this.getDescripcion(),
      tor_ttid: this.getTtid(),
      tor_fedid: this.getFedid(),
      tor_es_temporal: this.getEsTemporal(),
      tor_aplico_para_tira_inferiores: this.getAplicoParaTiraInferiores(),
      tor_tiene_lista_buena_fe: this.tieneListaBuenaFe,
      tor_cant_max_refuerzos: this.cantMaxRefuerzos,
      tor_multifederacion: this.multifederacion,
      faseList: this.getFaseList().map((f) => f.toJSON()),
      torneoTipo: null,
      tipoDefinicionXTorneoList: this.getTipoDefinicionXTorneoList().map((td) => td.toJSON()),
      categoria: null,
      division: null,
      federacion: null,
      temporada: null,
    };
  }

  getId(): string | null {
    return this.id;
  }

  getFaseList(): Fase[] {
    return this.faseList;
  }

  getNombre(): string {
    return this.nombre;
  }

  getFechaInicio(): Date {
    return this.fechaInicio;
  }

  getTemid(): number {
    return this.temid;
  }

  getCatid(): number {
    return this.catid;
  }

  getDivid(): number {
    return this.divid;
  }

  getRama(): string {
    return this.rama;
  }

  getCantAscensos(): number {
    return this.cantAscensos;
  }

  getTieneListaBuenaFe() {
    return this.tieneListaBuenaFe;
  }
  getCantMaxRefuerzos(): number {
    return this.cantMaxRefuerzos;
  }

  getCantDescensos(): number {
    return this.cantDescensos;
  }
  isMultifederacion() {
    return this.multifederacion;
  }

  getDescripcion(): any {
    return this.descripcion;
  }

  getTtid(): number {
    return this.ttid;
  }

  getFedid(): number {
    return this.fedid;
  }

  getEsTemporal() {
    return this.esTemporal;
  }

  getAplicoParaTiraInferiores() {
    return this.aplicoParaTiraInferiores;
  }

  getCategoria(): Categoria | null {
    return this.categoria;
  }
  getDivision(): Division | null {
    return this.division;
  }

  getTorneoTipo(): TorneoTipo | null {
    return this.torneoTipo;
  }

  getFederacion(): Federacion | null {
    return this.federacion;
  }
  getTemporada(): Temporada | null {
    return this.temporada;
  }

  getTipoDefinicionXTorneoList(): TipoDefinicionXTorneo[] {
    return this.tipoDefinicionXTorneoList;
  }

  setId(value: string | null): void {
    this.id = value;
  }

  setNombre(value: string): void {
    this.nombre = value;
  }

  setFechaInicio(value: Date): void {
    this.fechaInicio = value;
  }

  setTemid(value: number): void {
    this.temid = value;
  }

  setCatid(value: number): void {
    this.catid = value;
  }

  setDivid(value: number): void {
    this.divid = value;
  }

  setRama(value: string): void {
    this.rama = value;
  }

  setCantAscensos(value: number): void {
    this.cantAscensos = value;
  }

  setCantDescensos(value: number): void {
    this.cantDescensos = value;
  }

  setDescripcion(value: any): void {
    this.descripcion = value;
  }

  setTtid(value: number): void {
    this.ttid = value;
  }

  setFedid(value: number): void {
    this.fedid = value;
  }
}
